import type { ReactNode } from 'react';
import { CheckLine, GiftOutline } from '@/components/referral/icons';
import { formatBRL } from '@/lib/format';

/**
 * Telas que o INDICADO recebe — design "Bow Convite do Indicado" (Claude
 * Design · bow-convite.jsx), variantes "corrigido":
 *
 *  - `ConviteSheetView`  — sheet do claimer (sobre a tela atual, fundo
 *    desfocado): alça, lockup oficial, presente de linha, "Convite de {nome} /
 *    R$ X de cashback / no primeiro aporte", chip do código, CTA verde
 *    "Aceitar convite" + ghost "Agora não".
 *  - `ConviteLandingCard` — card navy sólido da landing /convite/:code, com
 *    brilho azul interno discreto, presente branco com faíscas, "Ganhe R$ X /
 *    de cashback no primeiro aporte" e CTA "Criar conta".
 *  - `CodeAppliedLine`    — "✓ Código X aplicado".
 *
 * Diagnóstico do design (não regredir): a espiral do espectro é a LOGO — não
 * vira borda nem glow; o espectro vive só no lockup real
 * (`/logo/bow-horizontal.png`, símbolo + wordmark minúsculo em UMA peça —
 * nunca recompor "arco + Bow digitado"); duas cores nas superfícies (navy +
 * verde de ação); o código é informação de apoio (chip cinza-navy), não
 * destaque. Componentes PUROS (props) pros smoke tests SSR.
 */

/** Lockup horizontal oficial (símbolo + wordmark "bow"), servido pelo Caddy. */
export const BOW_LOCKUP_SRC = '/logo/bow-horizontal.png';

const DISPLAY = 'Chillax, system-ui, sans-serif';

function moneyNoCents(value: string | number): string {
  return formatBRL(value).replace(',00', '');
}

/** Botão pílula verde cheio — CTA principal do convite (h 54). */
export function ConviteCta({
  children,
  onClick,
  disabled = false,
  type = 'button'
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-[54px] w-full items-center justify-center rounded-full bg-bow-green text-[16px] font-semibold tracking-[-0.01em] text-white transition hover:bg-bow-green/90 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Botão fantasma (h 46) — "Agora não" / "Já tenho conta". */
export function ConviteGhost({
  children,
  onClick,
  onDark = false,
  disabled = false
}: {
  children: ReactNode;
  onClick?: () => void;
  onDark?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-[46px] w-full rounded-full bg-transparent text-[15px] font-semibold disabled:opacity-60"
      style={{ color: onDark ? 'rgba(241,237,228,0.75)' : 'rgba(0,27,116,0.6)' }}
    >
      {children}
    </button>
  );
}

/** Chip cinza-navy "Código SAULO-QTY" (informação de apoio). */
export function CodeChip({ code }: { code: string }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3.5 py-[7px] text-[13px]"
      style={{ background: 'rgba(0,27,116,0.05)', color: 'rgba(0,27,116,0.6)' }}
    >
      Código{' '}
      <strong className="font-semibold tracking-[0.04em] text-bow-royal">{code}</strong>
    </span>
  );
}

/** "✓ Código SAULO-QTY aplicado" — confirmação discreta na landing. */
export function CodeAppliedLine({ code }: { code: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-[13px]" style={{ color: 'rgba(0,27,116,0.58)' }}>
      <span className="inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full bg-bow-green text-white">
        <CheckLine size={11} />
      </span>
      Código <strong className="font-semibold tracking-[0.04em] text-bow-royal">{code}</strong>{' '}
      aplicado
    </span>
  );
}

/* ───────────────────── A · Sheet do claimer ───────────────────── */

export interface ConviteSheetViewProps {
  referrerFirstName: string;
  code: string;
  welcomeBrl: string | number;
  busy?: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

/** Conteúdo do sheet (sem o overlay) — o container fixo fica no ReferralClaimer. */
export function ConviteSheetView({
  referrerFirstName,
  code,
  welcomeBrl,
  busy = false,
  onAccept,
  onDecline
}: ConviteSheetViewProps) {
  return (
    <SheetFrame>
      <div className="flex justify-center">
        <img src={BOW_LOCKUP_SRC} alt="Bow" className="block w-auto" style={{ height: 38 }} />
      </div>
      <div className="mt-[22px] flex justify-center">
        <GiftOutline size={62} />
      </div>
      <div className="mt-5 text-center">
        <div
          className="text-[11.5px] font-semibold uppercase tracking-[0.09em]"
          style={{ color: 'rgba(0,27,116,0.5)' }}
        >
          Convite de {referrerFirstName}
        </div>
        <div
          className="mt-[7px] text-bow-royal"
          style={{
            fontFamily: DISPLAY,
            fontWeight: 600,
            fontSize: 31,
            letterSpacing: '-0.03em',
            lineHeight: 1.05
          }}
        >
          {moneyNoCents(welcomeBrl)} de cashback
        </div>
        <div className="mt-1 text-[15.5px]" style={{ color: 'rgba(0,27,116,0.6)' }}>
          no primeiro aporte
        </div>
      </div>
      <div className="mt-4 flex justify-center">
        <CodeChip code={code} />
      </div>
      <div className="mt-[22px] flex flex-col gap-1">
        <ConviteCta onClick={onAccept} disabled={busy}>
          {busy ? 'Aplicando…' : 'Aceitar convite'}
        </ConviteCta>
        <ConviteGhost onClick={onDecline} disabled={busy}>
          Agora não
        </ConviteGhost>
      </div>
    </SheetFrame>
  );
}

/** Sheet branco com alça — raio 28 em cima, sombra pra cima, padding 12/26/26. */
export function SheetFrame({ children }: { children: ReactNode }) {
  return (
    <div
      className="w-full max-w-md overflow-hidden bg-white sm:mb-8 sm:rounded-[28px]"
      style={{
        borderRadius: '28px 28px 0 0',
        boxShadow: '0 -14px 40px rgba(0,27,116,0.18)',
        padding: '12px 26px 26px',
        paddingBottom: 'max(26px, env(safe-area-inset-bottom))'
      }}
    >
      <div
        aria-hidden
        className="mx-auto mb-5 h-1 w-[42px] rounded-full"
        style={{ background: 'rgba(0,27,116,0.16)' }}
      />
      {children}
    </div>
  );
}

/* ───────────────────── B · Landing /convite ───────────────────── */

export function ConviteLandingCard({
  referrerFirstName,
  welcomeBrl,
  onCreateAccount
}: {
  referrerFirstName: string;
  welcomeBrl: string | number;
  onCreateAccount: () => void;
}) {
  return (
    <div
      className="relative overflow-hidden text-bow-cream"
      style={{
        borderRadius: 30,
        background: '#001B74',
        padding: '34px 26px 30px',
        boxShadow: '0 14px 34px rgba(0,27,116,0.20)'
      }}
    >
      {/* Brilho azul interno discreto — no lugar do glow roxo + borda do espectro */}
      <span
        aria-hidden
        className="pointer-events-none absolute rounded-full"
        style={{
          right: -50,
          bottom: -70,
          width: 210,
          height: 190,
          background: 'radial-gradient(circle,#3A6BD8 0%,rgba(58,107,216,0) 70%)',
          opacity: 0.5,
          filter: 'blur(16px)'
        }}
      />
      <div className="relative flex flex-col items-center">
        <GiftOutline size={72} onDark sparks />
        <div
          className="mt-[22px] text-[11.5px] font-semibold uppercase tracking-[0.09em]"
          style={{ color: 'rgba(241,237,228,0.62)' }}
        >
          Convite de {referrerFirstName}
        </div>
        <div
          className="mt-[9px] text-white"
          style={{
            fontFamily: DISPLAY,
            fontWeight: 600,
            fontSize: 40,
            letterSpacing: '-0.035em',
            lineHeight: 1.02
          }}
        >
          Ganhe {moneyNoCents(welcomeBrl)}
        </div>
        <div className="mt-[7px] text-center text-[15.5px]" style={{ color: 'rgba(241,237,228,0.82)' }}>
          de cashback no primeiro aporte
        </div>
        <div className="mt-[26px] w-full">
          <ConviteCta onClick={onCreateAccount}>Criar conta</ConviteCta>
        </div>
      </div>
    </div>
  );
}
